import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import SelectionList from './SelectionList'

const DisabledStage = ({ stage }) => {
    return (
        <div key={stage.id} className="tw:flex tw:gap-4 tw:flex-col">
            <div className="tw:flex tw:justify-between tw:items-center tw:px-6">
                <div className="tw:inline-flex tw:gap-2 tw:items-center">
                    <div className="tw:size-3 tw:rounded-full tw:border-2 tw:border-gray-200"></div>
                    <span className="tw:text-sm tw:font-semibold">{stage.name}</span>
                </div>
            </div>
        </div>
    )
}

const Stage = ({
    stage,
    audiences,
    handleRemoveAudience,
    availableStages,
    audienceSelections,
    handleEditAudience,
    selectedAudienceToEdit,
}) => {
    const isDisabled = !availableStages.includes(stage.id)
    const [isOpen, setIsOpen] = useState(false)
    const selectedIds = audienceSelections[stage.id.toLowerCase()] || {}
    const collapseButtonText = isOpen ? 'Collapse All' : 'Expand All'
    const selectedAudience = audiences.filter((audience) => Object.values(selectedIds).includes(audience.id))
    let selectedAudienceIdToEdit = ''

    // If the stage is the same as the selectedAudienceToEdit, set the selectedAudienceIdToEdit to the audienceId
    // This is used to check the radio button for the selected audience
    if (stage.id == selectedAudienceToEdit?.stageId) {
        selectedAudienceIdToEdit = selectedAudienceToEdit?.audienceId
    }

    const toggleOpen = () => setIsOpen(!isOpen)

    if (isDisabled) return <DisabledStage stage={stage} />

    return (
        <div key={stage.id} className="tw:flex tw:gap-4 tw:flex-col">
            <div className="tw:flex tw:justify-between tw:items-center tw:px-6">
                <div className="tw:inline-flex tw:gap-2 tw:items-center" onClick={toggleOpen}>
                    <div className="tw:w-4">
                        <ChevronDown
                            className={cn(
                                'tw:size-4 tw:cursor-pointer tw:text-blue-300 tw:transition-transform tw:duration-300',
                                selectedAudience.length === 0 && 'tw:hidden',
                                isOpen && 'tw:rotate-180',
                            )}
                        />
                    </div>
                    <div className="tw:size-3 tw:rounded-full" style={{ backgroundColor: stage.color }}></div>
                    <span className="tw:text-sm tw:font-semibold tw:cursor-pointer">{stage.name}</span>
                </div>
                <span
                    className={cn(
                        'tw:text-sm tw:text-blue-300 tw:cursor-pointer',
                        selectedAudience.length === 0 && 'tw:hidden',
                    )}
                    onClick={toggleOpen}
                >
                    {collapseButtonText}
                </span>
            </div>

            <div
                className={cn(
                    'tw:overflow-hidden tw:transition-all tw:duration-300 tw:ease-in-out',
                    selectedAudience.length > 0 && isOpen ? 'tw:max-h-96 tw:opacity-100' : 'tw:max-h-0 tw:opacity-0',
                )}
            >
                {selectedAudience.length > 0 && (
                    <div className="tw:flex tw:flex-col tw:border-t tw:border-b tw:divide-y">
                        <SelectionList
                            isOpen={isOpen}
                            color={stage.color}
                            audiences={selectedAudience}
                            onEdit={(audienceId) => handleEditAudience(stage.id, audienceId)}
                            onRemove={(audienceId) => handleRemoveAudience(stage.id, audienceId)}
                            selectedAudienceIdToEdit={selectedAudienceIdToEdit}
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

const Stages = ({
    stages,
    audiences,
    handleRemoveAudience,
    handleEditAudience,
    availableStages,
    audienceSelections,
    selectedAudienceToEdit,
}) => {
    return (
        <>
            {stages.map((stage) => (
                <Stage
                    stage={stage}
                    key={stage.id}
                    audiences={audiences}
                    availableStages={availableStages}
                    audienceSelections={audienceSelections}
                    handleRemoveAudience={handleRemoveAudience}
                    handleEditAudience={handleEditAudience}
                    selectedAudienceToEdit={selectedAudienceToEdit}
                />
            ))}
        </>
    )
}

export default Stages
